import { useState, useEffect } from "react";
import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useKeycloak } from "../providers/keycloak-provider";
import { FaRegClock } from "react-icons/fa";
import SimplePagination from "../components/SimplePagination";
import { MdBlock, MdCheckCircle } from "react-icons/md";
import type { Employee, AfdData } from "../components/AfdResults";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import DashboardLayout, { LayoutProps } from "../components/DashboardLayout";

interface Props extends LayoutProps {
  dadosPath: string;
  onSelectEmployee?: (employee: Employee) => void;
}

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

export default function FuncionariosPage({ dadosPath, onSelectEmployee, csrfToken, flash }: Props) {
  const { keycloak } = useKeycloak();
  const tokenParsed = keycloak.tokenParsed as any;
  const clientId    = import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? "";
  const realmRoles  = (tokenParsed?.realm_access?.roles ?? []) as string[];
  const clientRoles = (tokenParsed?.resource_access?.[clientId]?.roles ?? []) as string[];
  const isAdmin     = [...realmRoles, ...clientRoles].includes("admin");
  const queryClient = useQueryClient();
  const [currentPage, setPage]    = useState(1);
  const [perPage, setPerPage]     = useState(PER_PAGE_OPTIONS[1]);
  const [search, setSearch]       = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "ignored">("active");
  const [confirmTarget, setConfirmTarget] = useState<Employee | null>(null);
  const [editTarget, setEditTarget] = useState<Employee | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim().length >= 3 ? search.trim() : "");
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const getCsrfToken = () =>
    document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "";

  // ── Query ────────────────────────────────────────────────────────────────
  const { data, isLoading, isError } = useQuery({
    queryKey: ["funcionarios", dadosPath, currentPage, perPage, debouncedSearch, statusFilter],
    queryFn: () => {
      const params = new URLSearchParams({
        page: String(currentPage),
        per_page: String(perPage),
      });
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (statusFilter !== "all") params.set("status", statusFilter);
      return axios
        .get<AfdData & { meta?: { total: number } }>(
          `${dadosPath}?${params.toString()}`,
          { headers: { Accept: "application/json", Authorization: `Bearer ${keycloak.token ?? ""}` } }
        )
        .then(res => res.data);
    },
    placeholderData: prev => prev,
  });

  const employees = data?.employees ?? [];
  const total     = data?.meta?.total ?? employees.length;
  const isSearching = search.trim().length > 0 && search.trim().length < 3;

  // ── Mutations ────────────────────────────────────────────────────────────
  const ignorarMutation = useMutation({
    mutationFn: (id: number) =>
      axios.patch<{ lista_ignorado: boolean }>(
        `/api/v1/funcionarios/${id}/ignorar`,
        {},
        { headers: { "X-CSRF-Token": getCsrfToken(), Accept: "application/json", Authorization: `Bearer ${keycloak.token ?? ""}` } }
      ).then(res => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["funcionarios"] });
      setConfirmTarget(null);
    },
    onError: () => setConfirmTarget(null),
  });

  const editMutation = useMutation({
    mutationFn: ({ id, chDiaria }: { id: number; chDiaria: number }) =>
      axios
        .patch<{ ch_diaria: number }>(
          `/funcionarios/${id}`,
          { ch_diaria: chDiaria },
          { headers: { "X-CSRF-Token": getCsrfToken(), Accept: "application/json", Authorization: `Bearer ${keycloak.token ?? ""}` } }
        )
        .then(res => res.data),
    onSuccess: (resData, { id }) => {
      queryClient.setQueryData<AfdData & { meta?: { total: number } }>(
        ["funcionarios", dadosPath, currentPage, perPage],
        old => old
          ? { ...old, employees: old.employees.map(e => e.id === id ? { ...e, ch_diaria: resData.ch_diaria } : e) }
          : old
      );
      setEditTarget(null);
    },
    onError: () => setEditTarget(null),
  });

  const handlePerPageChange = (n: number) => { setPerPage(n); setPage(1); };
  const handleSearchChange  = (q: string) => { setSearch(q);  setPage(1); };
  const handleStatusChange  = (v: "all" | "active" | "ignored") => { setStatusFilter(v); setPage(1); };

  const handleIgnorar = () => {
    if (!confirmTarget?.id) return;
    ignorarMutation.mutate(confirmTarget.id);
  };

  const handleEditSave = (chDiaria: number) => {
    if (!editTarget?.id) return;
    editMutation.mutate({ id: editTarget.id, chDiaria });
  };

  return (
    <DashboardLayout flash={flash}>
    <div className="max-w-6xl mx-auto space-y-2">

      {/* Dialog de edição de funcionário */}
      <Dialog open={editTarget !== null} onOpenChange={open => { if (!open) setEditTarget(null); }}>
        <DialogContent className="p-6">
          <DialogHeader>
            <DialogTitle className="font-bold">Carga Horária Diária</DialogTitle>
            <DialogDescription>{editTarget?.nome ?? "Sem nome"}</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={e => {
              e.preventDefault();
              if (editTarget) handleEditSave(Number((e.target as any).chDiaria.value));
            }}
            className="flex flex-col gap-4"
          >
            <div className="flex flex-col gap-1.5">
              <label htmlFor="ch-diaria-input" className="text-sm font-medium text-gray-700">
                Carga Horária Diária <span className="text-gray-400 font-normal">(horas por dia)</span>
              </label>
              <input
                id="ch-diaria-input"
                name="chDiaria"
                type="number"
                min={0}
                max={24}
                defaultValue={editTarget?.ch_diaria ?? 0}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" className="cursor-pointer hover:text-gray-600"  onClick={() => setEditTarget(null)} disabled={editMutation.isPending}>
                Cancelar
              </Button>
              <Button type="submit" variant="success" size="sm" className="hover:text-black" disabled={editMutation.isPending}>
                {editMutation.isPending ? "Salvando..." : "Salvar"}               
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmação */}
      <Dialog open={confirmTarget !== null} onOpenChange={open => { if (!open) setConfirmTarget(null); }}>
        <DialogContent className="p-6">
          <DialogHeader >
            <DialogTitle className="text-lg font-bold">
              {confirmTarget?.lista_ignorado ? "Remover da lista de inativos" : "Adicionar à lista de inativos"}
            </DialogTitle>
            <hr className="my-1" />
           <DialogDescription className="mt-2 text-sm text-gray-500">
            {confirmTarget?.lista_ignorado
              ? <>Tem certeza que deseja remover "<strong className="font-bold text-blue-900">{confirmTarget?.nome ?? confirmTarget?.pis}</strong>" da lista de inativos?</>  
              : <>Tem certeza que deseja adicionar "<strong className="font-bold text-blue-900">{confirmTarget?.nome ?? confirmTarget?.pis}</strong>" à lista de inativos?</>
            }

            <span className="block mt-2 text-xs text-gray-400">A lista de inativos é utilizada para excluir funcionários de relatórios e análises, mas eles permanecerão no sistema para fins de histórico e auditoria.</span>
          </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" size="sm" className="cursor-pointer hover:text-gray-600" onClick={() => setConfirmTarget(null)} disabled={ignorarMutation.isPending}>
              Cancelar
            </Button>
            <Button variant="destructive" size="sm" onClick={handleIgnorar} disabled={ignorarMutation.isPending} className="hover:text-white">
              {ignorarMutation.isPending ? (
                <svg className="animate-spin w-4 h-4 mr-1 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
              ) : null}
              {confirmTarget?.lista_ignorado ? "Sim, remover" : "Sim, adicionar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Header ── */}
      <div className="p-6 border-b border-gray-100 bg-gray-50 shadow-sm " >
        <header>
          <div className="w-full flex items-between gap-6 flex-wrap">
            <div className="flex justify-between items-center w-full">
              <h1 className="text-2xl font-bold text-gray-800">
                Funcionários
                <span className="ml-2 text-xs font-normal text-gray-400">({total} registros no total)</span>
              </h1>
            </div>

            <div className="flex items-start gap-3 bg-blue-50/50 border-l-4 border-blue-400 p-4 rounded-r-lg w-full">
              <div className="flex-shrink-0 mt-0.5">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">
                <span className="font-semibold text-slate-700 block md:inline mb-1 md:mb-0">
                  Gestão de Funcionários:
                </span>
                {" "}Exiba e gerencie os colaboradores cadastrados. Você pode adicionar funcionários à
                <span className="font-medium text-blue-700"> lista de inativos</span> para que eles não sejam incluídos na lista do ponto.
              </p>
            </div>
          </div>

          <div className="flex gap-3 mt-4 flex-wrap justify-end">
            <div className="flex-[3] max-w-[340px]">
              <label className="text-[11px] font-semibold text-gray-400 mb-1.5 ml-1 block">
                Buscar por NOME ou PIS
              </label>
              <input
                type="search"
                value={search}
                onChange={e => handleSearchChange(e.target.value)}
                placeholder="Ex: Carlos ou 12345678900"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white
                focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
              />
              {isSearching && (
                <p className="text-[11px] text-amber-500 mt-1 ml-1">Digite pelo menos 3 caracteres para buscar.</p>
              )}
            </div>
            <div className="flex-[1] max-w-[200px]">
              <label className="text-[11px] font-semibold text-gray-400 mb-1.5 ml-1 block">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={e => handleStatusChange(e.target.value as "all" | "active" | "ignored")}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white
                focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none cursor-pointer"
              >
                <option value="all">Todos</option>
                <option value="active">Ativos</option>
                <option value="ignored">Inativos</option>
              </select>
            </div>
          </div>
        </header>
      </div>

      {/* ── Table card ── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center gap-3 py-12 text-gray-400">
            <svg className="animate-spin w-4 h-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            <span className="text-sm">Carregando...</span>
          </div>
        ) : isError ? (
          <p className="text-center text-red-500 text-sm py-10">Erro ao carregar funcionários. Tente novamente.</p>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-blue-50 text-gray-600 text-center">
                  <th className="px-4 py-3 font-semibold border-b border-gray-200 w-12 ">#</th>
                  <th className="px-4 py-3 font-semibold border-b border-gray-200 text-left">Nome</th>
                  <th className="px-4 py-3 font-semibold border-b border-gray-200 text-center">PIS</th>
                  <th className="px-4 py-3 font-semibold border-b border-gray-200 text-center">Ch. Diária</th>
                  <th className="px-4 py-3 font-semibold border-b border-gray-200 text-center">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {employees.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-10 text-center text-sm text-gray-400">
                      Nenhum funcionário encontrado.
                    </td>
                  </tr>
                ) : employees.map((emp, i) => {
                  const globalIndex = (currentPage - 1) * perPage + i + 1;
                  return (
                    <tr
                      key={emp.pis}
                      className={`hover:bg-gray-50 transition-colors${onSelectEmployee ? " cursor-pointer" : ""}`}
                      onClick={() => onSelectEmployee?.(emp)}
                    >
                      <td className="px-4 py-3 text-gray-400 text-xs text-right">{globalIndex}</td>
                      <td className="px-4 py-3 font-medium text-gray-800">
                        {emp.nome ?? <span className="text-gray-400 italic">Sem nome</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-600 font-mono text-xs text-center">{emp.pis}</td>
                      <td className="px-4 py-3 text-gray-600 text-sm text-center">{emp.ch_diaria > 0 ? `${emp.ch_diaria}h` : "-"}</td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            onClick={e => { e.stopPropagation(); setEditTarget(emp); }}
                            variant="outline"
                            className="cursor-pointer flex items-center gap-1"
                            size="sm"
                            title="Editar carga horaria diária"
                            disabled={!isAdmin}
                          ><FaRegClock />  CH                           
                          </Button>
                          <Button
                            onClick={e => { e.stopPropagation(); setConfirmTarget(emp); }}
                            className="cursor-pointer flex items-center gap-1"
                            variant={emp.lista_ignorado ? "destructive" : "success"}
                            size="sm"
                            title={emp.lista_ignorado ? "Remover da lista de inativos" : "Adicionar a lista de inativos"}
                            disabled={!isAdmin}
                          >
                          {emp.lista_ignorado ? (
                              <span style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'white' }}>
                                <MdBlock /> Inativo
                              </span>
                            ) : (
                              <span style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'white', fontWeight: 'bold'}} >
                                <MdCheckCircle /> Ativo
                              </span>
                            )}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="px-5 border-t border-gray-100">
              <SimplePagination
                totalItems={total}
                itemsPerPage={perPage}
                currentPage={currentPage}
                onPageChange={setPage}
                onPerPageChange={handlePerPageChange}
                perPageOptions={PER_PAGE_OPTIONS}
              />
            </div>
          </>
        )}
      </div>
    </div>
    </DashboardLayout>
  );
}
